<script lang="ts">
import type { Component, ComponentPublicInstance } from 'vue';
import type { AnyItem, CompositePrimaryKey, DisplayTemplateAst, FieldSelection, GlobalSingleRelationFieldId } from '#layers/data-model/public';
import type { LayoutSortingState, SharedLayoutEmits, SharedLayoutProps } from '#layers/listing/types/layout';

export interface CardsDisplayOptions {
	titleTemplate: DisplayTemplateAst | null;
	descriptionTemplate: DisplayTemplateAst | null;
	thumbnailField: GlobalSingleRelationFieldId | null;
	cardSize: 'sm' | 'md' | 'lg';
	sorting?: LayoutSortingState;
	pageSize?: number;
}

export interface LayoutCardsProps extends SharedLayoutProps<CardsDisplayOptions> {
	renderDisplayTemplate: Component;
}

export interface LayoutCardsEmits extends SharedLayoutEmits {
	'open': [key: CompositePrimaryKey];
	'resetFilters': [];
	'emptyStateAction': [];
	'resetView': [];
	'update:displayOptions': [displayOptions: Record<string, unknown>];
}
</script>

<script setup lang="ts">
import { useResizeObserver } from '@vueuse/core';
import { computed, ref, toRef, useTemplateRef, watch } from 'vue';
import { useDisplayTemplateFieldSelection, useFieldSelectionContexts } from '#layers/blocks/public';
import { useItems } from '#layers/collection-query/public';
import { CollectionReads, getCollectionPrimaryKeyFields, getItemPrimaryKey, idFieldSelectionToFieldSelection, mergeFieldSelections, resolveFieldDisplayFieldSelection, stringifyStableId, useSingleRelationField, useWorkspaceSchemaStore } from '#layers/data-model/public';
import LayoutDefaultEmptyState from '#layers/listing/components/Layout/LayoutDefaultEmptyState.vue';
import { injectListingContext } from '#layers/listing/components/Listing/context';
import ListingErrorState from '#layers/listing/components/Listing/ErrorState.vue';
import { useConvertLayoutSortingStateToQuerySort } from '#layers/listing/composables/use-convert-layout-sorting-state-to-query-sort';
import { exposeListingQueryToBlockRootContext } from '#layers/listing/composables/use-listing-query-block-root-context';
import { useSelection } from '#layers/listing/composables/use-selection';
import { useSortGate } from '#layers/listing/composables/use-sort-gate';
import { LISTING_LOADING_DELAY_MS } from '#layers/listing/constants/loading';
import { DEFAULT_LISTING_PAGE_SIZE, useDeferredTrue, useGridNavigation } from '#layers/ui/public';
import LayoutCardsDisplayOptions, { initializeLayoutCardsOptions } from './LayoutCardsDisplayOptions.vue';
import LayoutCardsTile from './LayoutCardsTile.vue';

const {
	collection: collectionProp,
	filter: filterProp,
	displayOptions: displayOptionsProp,
	selectionKeys,
	projectionKeys,
	selectionMode,
	searchTerm,
} = defineProps<LayoutCardsProps>();
const emit = defineEmits<LayoutCardsEmits>();

const collection = toRef(() => collectionProp);
const filter = toRef(() => filterProp);

const { setDisplayOptionsComponent } = injectListingContext();
setDisplayOptionsComponent(LayoutCardsDisplayOptions);

const sortGate = useSortGate(() => collectionProp);
const workspaceSchema = useWorkspaceSchemaStore();
const displayOptions = computed(() => initializeLayoutCardsOptions({ collection: collectionProp, options: displayOptionsProp, sortDirections: sortGate.directions, querySchema: workspaceSchema.querySchema }));

const titleTemplate = computed(() => displayOptions.value.titleTemplate);
const descriptionTemplate = computed(() => displayOptions.value.descriptionTemplate);
const thumbnailField = useSingleRelationField(() => displayOptions.value.thumbnailField);

const titleFieldSelection = useDisplayTemplateFieldSelection(collection, CollectionReads.Many, () => titleTemplate.value ?? undefined);
const descriptionFieldSelection = useDisplayTemplateFieldSelection(collection, CollectionReads.Many, () => descriptionTemplate.value ?? undefined);

const { fieldSelectionCtx, idCtx } = useFieldSelectionContexts();

const selection = defineModel<CompositePrimaryKey[]>('selection', { default: () => [] });
const primaryKeyFields = computed(() => getCollectionPrimaryKeyFields(collection.value));

const { selectionFields, isSelectionEmpty, isSelected, toggleSelection, clear: clearSelection } = useSelection({
	selection,
	primaryKeyFields,
	selectionKeys: () => selectionKeys,
	projectionKeys: () => projectionKeys,
	selectionMode: () => selectionMode,
});

const querySelection = computed<FieldSelection>(() => {
	const selections: FieldSelection[] = [titleFieldSelection.value, descriptionFieldSelection.value, { fields: [...primaryKeyFields.value, ...selectionFields.value].map((field) => field.apiName) }];

	if (thumbnailField.value) {
		const thumbIdSelection = resolveFieldDisplayFieldSelection(thumbnailField.value, collection.value, fieldSelectionCtx);
		selections.push(idFieldSelectionToFieldSelection(thumbIdSelection, idCtx));
	}

	return mergeFieldSelections(...selections);
});

const pageSize = computed({
	get: () => displayOptions.value.pageSize ?? DEFAULT_LISTING_PAGE_SIZE,
	set: (value) => emit('update:displayOptions', { ...displayOptionsProp, pageSize: value }),
});

const { convert: convertSort } = useConvertLayoutSortingStateToQuerySort(sortGate.directions);

const { items, pageNumber, totalItemsCount, currentItemsCount, filteredItemsCount, showLoading, error, refresh, query: itemsQuery } = useItems({
	collection,
	errorOnNonPermittedField: () => Boolean(selectionKeys?.length || projectionKeys?.length),
	query: () => ({
		filter: filter.value,
		...querySelection.value,
		search: searchTerm,
		sort: convertSort(displayOptions.value.sorting),
	}),
	pageSize,
});

exposeListingQueryToBlockRootContext(() => ({
	collection: collection.value,
	query: itemsQuery.value,
}));

const showSpinner = useDeferredTrue(showLoading, LISTING_LOADING_DELAY_MS);

watch(pageNumber, () => {
	clearSelection();
});

const mappedData = computed(() => items.value.map((item: AnyItem) => {
	let file;

	if (thumbnailField.value) {
		file = item[thumbnailField.value.apiName];
	}

	return ({
		primaryKey: stringifyStableId(getItemPrimaryKey(item, collection.value)),
		item,
		file,
	});
}));

const cardSizeMultipliers = {
	sm: 20,
	md: 30,
	lg: 40,
};

const cardSize = computed(() => {
	const size = displayOptions.value.cardSize;
	const multiplier = size && size in cardSizeMultipliers ? cardSizeMultipliers[size] : cardSizeMultipliers.md;
	return `calc(var(--spacing) * ${multiplier})`;
});

function handleTileClick(item: AnyItem, index: number) {
	setFocused(index);

	if (isSelectionEmpty.value && !selectionMode) {
		emit('open', getItemPrimaryKey(item, collection.value));
	}
	else {
		toggleSelection(item);
	}
}

function handleTileSelect(item: AnyItem, index: number) {
	setFocused(index);
	toggleSelection(item);
}

const gridContainerRef = useTemplateRef<HTMLElement>('gridContainer');
const columns = ref(1);

function recomputeColumns() {
	const container = gridContainerRef.value;

	if (!container) return;

	const tracks = getComputedStyle(container).gridTemplateColumns.split(/\s+/).filter(Boolean);
	columns.value = Math.max(1, tracks.length);
}

useResizeObserver(gridContainerRef, recomputeColumns);

const total = computed(() => mappedData.value.length);

const {
	focusedIndex,
	tabbableIndex,
	registerItem,
	setFocused,
	handleArrowDown,
	handleArrowLeft,
	handleArrowRight,
	handleArrowUp,
	handleActivate,
	handleSelect,
} = useGridNavigation({
	columns,
	total,
	onActivate: () => activateFocusedTile(),
	onSelect: () => selectFocusedTile(),
});

function activateFocusedTile() {
	const card = mappedData.value[focusedIndex.value];

	if (!card) return;

	if (isSelectionEmpty.value && !selectionMode) {
		emit('open', getItemPrimaryKey(card.item, collection.value));
	}
	else {
		toggleSelection(card.item);
	}
}

function selectFocusedTile() {
	const card = mappedData.value[focusedIndex.value];

	if (card) toggleSelection(card.item);
}

function registerTileAtIndex(index: number, instance: Element | ComponentPublicInstance | null) {
	const element = instance && '$el' in instance ? instance.$el : instance;
	registerItem(index, element instanceof HTMLElement ? element : null);
}
</script>

<template>
	<div class="flex h-full flex-col overflow-hidden">
		<div class="flex-1 overflow-y-auto">
			<div v-if="showSpinner && mappedData.length === 0" class="flex flex-1 items-center justify-center py-12">
				<DSpinner class="size-6 text-muted" />
			</div>
			<!-- Ahead of the cards: they answer whichever query last succeeded, so leaving them up would present them as the result of the one that just failed. -->
			<ListingErrorState
				v-else-if="error"
				:error
				:can-reset-view
				class="h-full"
				@retry="refresh"
				@reset-view="$emit('resetView')"
			/>
			<div
				v-else-if="mappedData.length > 0"
				ref="gridContainer"
				class="grid grid-cols-[repeat(auto-fit,var(--card-size))] gap-4 p-2 px-6 py-4"
				:style="{ '--card-size': cardSize }"
				@keydown.down.prevent="handleArrowDown"
				@keydown.enter.prevent="handleActivate"
				@keydown.left.prevent="handleArrowLeft"
				@keydown.right.prevent="handleArrowRight"
				@keydown.space.prevent="handleSelect"
				@keydown.up.prevent="handleArrowUp"
			>
				<LayoutCardsTile
					v-for="(card, index) in mappedData"
					:key="card.primaryKey"
					:ref="(instance) => registerTileAtIndex(index, instance)"
					:item="card.item"
					:file="card.file"
					:title-template
					:description-template
					:collection-id="collection.id"
					:size="displayOptions.cardSize"
					:selected="isSelected(card.item)"
					:selection-mode
					:is-selection-empty
					:focused="index === tabbableIndex"
					:render-display-template
					@focus="setFocused(index)"
					@select="handleTileSelect(card.item, index)"
					@click="handleTileClick(card.item, index)"
				/>
			</div>
			<LayoutDefaultEmptyState
				v-else-if="!showLoading"
				:total-items-count
				:filtered-items-count
				:empty-state
				@reset-filters="$emit('resetFilters')"
				@empty-state-action="$emit('emptyStateAction')"
			/>
		</div>

		<DPaginationBar
			v-model:page="pageNumber"
			v-model:page-size="pageSize"
			:total-items="totalItemsCount"
			:current-items="currentItemsCount"
			:loading="showLoading"
			class="border-t"
		/>
	</div>
</template>
